import React, { useState, useEffect } from 'react';

// Redux
import { useDispatch } from 'react-redux';

// Axios

// Formik
import { useFormik, Formik } from 'formik';

// Validation
import { stopSchema } from 'yupSchemas';

// Debounce
import { useDebouncedCallback } from 'use-debounce';

// Nominatim -> A browser-usable client for Open Street Map's Nominatim service.
import * as Nominatim from 'nominatim-browser';

// Material
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

// Actions
import * as StopsActions from 'store/ducks/stops/actions';

// Helpers
import { handleResults } from 'helpers/handleResults';
import * as LeafletHelpers from 'helpers/leaflet';

// Components
import { NewAddressForm } from 'components/Forms/NewFormAddress';

// Constants
import STOPS_CONST from 'constants/stops';
import MAP_CONSTANTS from 'constants/map';

// Leaflet
import L from 'leaflet';
import { Map, TileLayer, Marker } from 'react-leaflet';
import { SearchControl, HereProvider } from 'components/LeafletMapSearch';
import * as MarkerIcons from 'components/Leaflet/MarkerIcons';

// Styles
import useStyles from './formStop.styles';

const searchControlProvider = HereProvider();

interface FormStopProps {
  geojson?: any;
}
interface AddressInterface {
  city_district: string;
  country: string;
  city: string;
  country_code: string;
  municipality: string;
  postcode: string;
  region: string;
  road: string;
  state: string;
  neighbourhood: string;
  state_district: string;
  suburb: string;
  town: string;
}
interface PlaceNominatim {
  place_id: number;
  display_name: string;
  address: AddressInterface;
  lat: string;
  lon: string;
}

type Props = FormStopProps;

const FormStop: React.FC<Props> = ({ geojson }) => {
  const classes = useStyles();

  const dispatch = useDispatch();

  const [mapRef, setMapRef] = useState<Map | null>(null);
  const [options, setOptions] = useState<PlaceNominatim[]>([]);
  const initialValues = {
    name: '',
    startLat: '',
    startLong: '',
    endLat: '',
    endLong: '',
    stop: 0,
    busBay: 0,
    specialBusStop: false,
    visible: true, // default true
    covered: 0,
    radius: 0,
    address: {
      cep: '',
      street: '',
      number: '',
      compl: '',
      neigh: '',
      city: '',
      state: '',
    },
  };
  type FormType = typeof initialValues;

  const handleSubmit = (values: FormType) => {
    dispatch(
      StopsActions.createStopRequest({
        name: values.name,
        address: values.address,
        startLat: Number(values.startLat),
        startLong: Number(values.startLong),
        endLat: Number(values.endLat),
        endLong: Number(values.endLong),
        busBay: values.busBay,
        specialBusStop: Boolean(values.specialBusStop),
        visible: Boolean(values.visible),
        radius: values.radius,
        covered: values.covered,
      }),
    );
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
  });

  // Load Geojson
  useEffect(() => {
    if (mapRef && geojson) {
      const lfMap = mapRef.leafletElement;

      LeafletHelpers.clearMap(lfMap);

      LeafletHelpers.loadGeoJsonLine({ mapRef: lfMap, geojson });

      LeafletHelpers.centerMap(lfMap);
    }
  }, [mapRef, geojson]);

  const [debouncedCallbackData] = useDebouncedCallback((value: string) => {
    Nominatim.geocode({
      q: value,
      addressdetails: true,
    })
      .then((data: PlaceNominatim[]) => {
        if (!data.length) {
          setOptions([]);
          return;
        }
        setOptions(data);
      })
      .catch((error: any) => {
        setOptions([]);
      });
  }, 800);

  const handleAddMarker = async ({
    lat,
    lng,
  }: {
    lat: number;
    lng: number;
  }) => {
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      // try {
      //   const algoliaPlacesURL = `${ENVS.ALGOLIA_PLACES_URL}/reverse`;
      //   const parsedLat = parseFloat(String(lat));
      //   const parsedLng = parseFloat(String(lng));
      //   const { data, status } = await axios.get(algoliaPlacesURL, {
      //     params: {
      //       aroundLatLng: `${parsedLat},${parsedLng}`,
      //       hitsPerPage: 1,
      //       language: 'pt',
      //     },
      //   });
      //   if (
      //     status === 200 &&
      //     Array.isArray(data.hits) &&
      //     data.hits.length > 0
      //   ) {
      //     handleResultsAlgolia(data.hits[0], formik);
      //   }
      // } catch (err) {
      //   // Do nothing!!
      // }
    }
    debouncedCallbackData(`${lat},${lng}`);

    formik.setFieldValue('startLat', parseFloat(lat.toString()));
    formik.setFieldValue('endLat', parseFloat(lat.toString()));
    formik.setFieldValue('startLong', parseFloat(lng.toString()));
    formik.setFieldValue('endLong', parseFloat(lng.toString()));
  };

  const handleClickMap = (event: L.LeafletMouseEvent) => {
    const markerIcon = document.querySelector(
      '.leaflet-pane.leaflet-marker-pane img.custom_marker_icon_leaflet_geosearch',
    );

    if (markerIcon) {
      markerIcon.setAttribute('style', 'display: none');
    }

    const { lat, lng } = event.latlng;

    handleAddMarker({ lat, lng });
  };

  useEffect(() => {
    if (options.length > 0) {
      formik.setFieldValue('address', {
        ...formik.values.address,
        cep: options[0]?.address.postcode,
        street: options[0]?.address.road,
        neigh: options[0]?.address.suburb,
        city: options[0]?.address.city,
        state: options[0]?.address.state,
      });
    }
  }, [options]);

  return (
    <Formik
      initialValues={{ ...initialValues }}
      validationSchema={stopSchema}
      onSubmit={handleSubmit}>
      {(formik) => (
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography variant='h4' color='primary'>
                Nova Parada
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <TextField
                name='name'
                label='Nome'
                fullWidth
                variant='outlined'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.name}
                error={Boolean(formik.errors?.name && formik.touched?.name)}
                helperText={
                  formik.errors?.name &&
                  formik.touched?.name &&
                  formik.errors?.name
                }
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <TextField
                name='covered'
                label='Coberto'
                select
                fullWidth
                variant='outlined'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.covered}
                error={Boolean(
                  formik.errors?.covered && formik.touched?.covered,
                )}
                helperText={
                  formik.errors?.covered &&
                  formik.touched?.covered &&
                  formik.errors?.covered
                }>
                {STOPS_CONST.COVERED.map((COVERED) => (
                  <MenuItem
                    key={`op-covered-${COVERED.KEY}`}
                    value={COVERED.KEY}>
                    {COVERED.VALUE}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <TextField
                name='busBay'
                label='Baia'
                select
                fullWidth
                variant='outlined'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.busBay}
                error={Boolean(formik.errors?.busBay && formik.touched?.busBay)}
                helperText={
                  formik.errors?.busBay &&
                  formik.touched?.busBay &&
                  formik.errors?.busBay
                }>
                {STOPS_CONST.BUSBAY.map((BUSBAY) => (
                  <MenuItem key={`op-busBay-${BUSBAY.KEY}`} value={BUSBAY.KEY}>
                    {BUSBAY.VALUE}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <TextField
                name='specialBusStop'
                label='Destacar Ponto no Mapa'
                select
                fullWidth
                variant='outlined'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.specialBusStop}
                error={Boolean(
                  formik.errors?.specialBusStop &&
                    formik.touched?.specialBusStop,
                )}
                helperText={
                  formik.errors?.specialBusStop &&
                  formik.touched?.specialBusStop &&
                  formik.errors?.specialBusStop
                }>
                {STOPS_CONST.SPECIAL_BUS_STOP.map((SPECIAL_BUS_STOP) => (
                  <MenuItem
                    key={`op-specialBusStop-${SPECIAL_BUS_STOP.KEY}`}
                    value={SPECIAL_BUS_STOP.KEY}>
                    {SPECIAL_BUS_STOP.VALUE}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <TextField
                name='visible'
                label='Venda de Passagens'
                select
                fullWidth
                variant='outlined'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.visible}
                error={Boolean(
                  formik.errors?.visible && formik.touched?.visible,
                )}
                helperText={
                  formik.errors?.visible &&
                  formik.touched?.visible &&
                  formik.errors?.visible
                }>
                {STOPS_CONST.SPECIAL_BUS_STOP.map((SPECIAL_BUS_STOP) => (
                  <MenuItem
                    key={`op-visible-${SPECIAL_BUS_STOP.KEY}`}
                    value={SPECIAL_BUS_STOP.KEY}>
                    {SPECIAL_BUS_STOP.VALUE}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <TextField
                error={Boolean(formik.errors.radius && formik.touched.radius)}
                type='number'
                inputProps={{ min: 0 }}
                fullWidth
                helperText={
                  formik.errors.radius &&
                  formik.touched.radius &&
                  formik.errors.radius
                }
                id='text-field-radius'
                label='Raio'
                name='radius'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.radius}
                variant='outlined'
              />
            </Grid>
            <Grid item lg={12} md={12} sm={12} xl={12} xs={12}>
              <Typography style={{ fontWeight: 'bold' }}>
                Clique no mapa ou digite o endereço para indicar uma posição
              </Typography>
              <Box height={300}>
                <Map
                  ref={(ref: any) => setMapRef(ref)}
                  zoom={5}
                  onClick={handleClickMap}
                  style={{ height: '100%', width: '100%' }}
                  center={MAP_CONSTANTS.DEFAULT_CENTER as any}>
                  <TileLayer
                    url={MAP_CONSTANTS.CONTRIBUTION_ICON}
                    attribution={MAP_CONSTANTS.CONTRIBUTION_LINK}
                  />
                  {formik.values &&
                    formik.values.startLat &&
                    formik.values.startLong && (
                      <Marker
                        icon={MarkerIcons.redIcon}
                        position={[
                          parseFloat(formik.values.startLat),
                          parseFloat(formik.values.startLong),
                        ]}
                      />
                    )}
                  <SearchControl
                    provider={searchControlProvider}
                    autoClose
                    keepResult
                    animateZoom
                    maxMarkers={1}
                    showPopup={false}
                    showMarker={false}
                    retainZoomLevel={false}
                    searchLabel='Por favor, digite o CEP ou endereço'
                    popupFormat={({ query, result }: any) => {
                      debouncedCallbackData(
                        `${parseFloat(result.y)},${parseFloat(result.x)}`,
                      );
                      if (result.raw) {
                        handleResults(result.raw.address, formik);
                      }

                      handleAddMarker({
                        lat: parseFloat(result.y),
                        lng: parseFloat(result.x),
                      });

                      formik.setFieldValue('startLat', parseFloat(result.y));
                      formik.setFieldValue('startLong', parseFloat(result.x));

                      formik.setFieldValue('endLat', parseFloat(result.y));
                      formik.setFieldValue('endLong', parseFloat(result.x));
                    }}
                  />
                </Map>
              </Box>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xl={12} xs={12}>
              <NewAddressForm
                spacing={3}
                formik={formik}
                className={classes.input}
                fieldName='address'
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <TextField
                type='number'
                name='startLat'
                label='Latitude Inicial'
                fullWidth
                variant='outlined'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.startLat}
                error={Boolean(
                  formik.errors?.startLat && formik.touched?.startLat,
                )}
                helperText={
                  formik.errors?.startLat &&
                  formik.touched?.startLat &&
                  formik.errors?.startLat
                }
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <TextField
                type='number'
                name='startLong'
                label='Longitude Inicial'
                fullWidth
                variant='outlined'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.startLong}
                error={Boolean(
                  formik.errors?.startLong && formik.touched?.startLong,
                )}
                helperText={
                  formik.errors?.startLong &&
                  formik.touched?.startLong &&
                  formik.errors?.startLong
                }
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <TextField
                type='number'
                name='endLat'
                label='Latitude Final'
                fullWidth
                variant='outlined'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.endLat}
                error={Boolean(formik.errors?.endLat && formik.touched?.endLat)}
                helperText={
                  formik.errors?.endLat &&
                  formik.touched?.endLat &&
                  formik.errors?.endLat
                }
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <TextField
                type='number'
                name='endLong'
                label='Longitude Final'
                fullWidth
                variant='outlined'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.endLong}
                error={Boolean(
                  formik.errors?.endLong && formik.touched?.endLong,
                )}
                helperText={
                  formik.errors?.endLong &&
                  formik.touched?.endLong &&
                  formik.errors?.endLong
                }
              />
            </Grid>
          </Grid>
          <Grid className={classes.rowButton} container spacing={2}>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <Button color='primary' variant='contained' type='submit'>
                Criar
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};

export { FormStop };
export default FormStop;
